import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Compass, Mail, Lock, ArrowRight, Gift } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      if (user.role === "admin") {
        navigate("/admin", { replace: true });
      } else if (!user.onboarding_complete) {
        navigate("/onboarding", { replace: true });
      } else {
        navigate("/compass", { replace: true });
      }
    }
  }, [user, navigate]);

  // Show loading state while redirecting
  if (user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground">Redirecting...</div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success("Welcome back!");
      
      if (userData.role === "admin") {
        navigate("/admin");
      } else if (!userData.onboarding_complete) {
        navigate("/onboarding");
      } else {
        navigate("/compass");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/compass";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 py-12 lg:px-16">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img 
              src="/compass-logo.png" 
              alt="Compass" 
              className="w-12 h-12 object-contain"
            />
            <span className="font-heading text-3xl md:text-4xl text-foreground">Compass</span>
          </Link>

          <div className="animate-fade-in">
            <h1 className="text-2xl md:text-3xl text-foreground mb-2">Welcome back</h1>
            <p className="text-muted-foreground mb-8">Continue your transformation journey.</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-12"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-12"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full py-6"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-background text-muted-foreground">or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="btn-secondary w-full py-6"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>

            {/* Check if coming from trial link - show prominent register link */}
            {(() => {
              const redirectParam = searchParams.get('redirect');
              const hasPendingTrial = sessionStorage.getItem('compass_pending_trial_code');
              const isTrialRedirect = redirectParam?.includes('/trial?code=') || hasPendingTrial;
              
              if (isTrialRedirect) {
                return (
                  <div className="mt-8 p-4 rounded-xl bg-gradient-to-r from-[#a69373]/20 to-[#a69373]/10 border border-[#a69373]/30 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Gift className="w-5 h-5 text-[#a69373]" />
                      <span className="text-white font-medium">Trial Code Detected</span>
                    </div>
                    <p className="text-white/70 text-sm mb-3">Already have an account? Log in above.</p>
                    <Link 
                      to="/register" 
                      className="inline-flex items-center gap-2 text-[#a69373] hover:text-[#a69373]/80 font-medium"
                    >
                      New here? Create an account <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                );
              }
              
              return (
                <p className="mt-8 text-center text-muted-foreground">
                  Don&apos;t have an account?{" "}
                  <Link to="/register" className="text-primary hover:underline">
                    Begin your journey
                  </Link>
                </p>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Right side - Visual with Sunflower */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(/images/sunflower-cosmic.png)',
            backgroundPosition: 'center center'
          }}
        />
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2d4a44]/90 via-transparent to-transparent" />
        {/* Quote content - positioned at bottom center */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-center p-12 pb-16">
          <div className="text-center max-w-md">
            <blockquote className="text-2xl font-heading font-bold text-foreground italic mb-4">
              &ldquo;Most people are living by a map they never chose.&rdquo;
            </blockquote>
            <p className="text-muted-foreground font-bold">— TrueNorth Zone</p>
          </div>
        </div>
      </div>
    </div>
  );
}
